import React, { ReactNode, useEffect, useState, MouseEvent } from 'react';
import styled from 'styled-components';
// --------------------------------------------------------------------------------
import { config, Icons } from '../index';
// --------------------------------------------------------------------------------
import { String, StringProps } from './String';
import { CardProps } from './Card';
import { useUniqId } from '../hooks/useUniqId';

// --------------------------------------------------------------------------------
// 📌  Default style configuration for Accordion
// --------------------------------------------------------------------------------
const styleConfig = {
  borderRadius: config.spacingSmall,
  backgroundColor: config.transparent,
  border: `1px solid ${config.muted}`,
  padding: config.spacingXMedium,
  color: config.darkMuted,
  iconSize: 16,
  collapseAll: false, // allows for children to be collapsed individually
  iconRotateAngle: -45,
  gap: config.spacingSmall,
};

export interface AccordionProps extends CardProps {
  /* Accordion object */
  accordion?: AccordionItem[];
  /* Spacing between accordions */
  gap?: string;
  /* Accordion animation */
  collapseAll?: boolean;
  /* Accordion width */
  width?: string;
  /* Accordion content padding */
  padding?: string;
  // --------------------------------------------------------------------------------
  /* Accordion onClick callback action */
  onClick?: (event?: MouseEvent) => void;
}

export interface ToggleProps {
  idx: number;
}
export interface AccordionItem extends CardProps {
  title?: StringProps[];
  content?: ContentProps[];
  // --------------------------------------------------------------------------------
  isOpen?: boolean;
  setActive?: boolean;
  icon?: ReactNode;
  iconSize?: number;
  iconColor?: string;
  iconStartAngle?: number;
  iconRotateAngle?: number;
  width?: string;
  headBorder?: string;
  // --------------------------------------------------------------------------------
  idx?: number;
  indxArray?: number[];
  setIndxArray?: React.Dispatch<React.SetStateAction<number[]>>;
  setCurrentIdx?: React.Dispatch<React.SetStateAction<number>>;
  collapseAll?: boolean;
}

export interface ContentProps {
  node?: ReactNode;
  body?: StringProps;
  width?: string;
}

const StyledAccordionWrapper = styled.div<AccordionItem>`
  display: flex;
  flex-direction: column;
  flex: 1;

  border: ${(props) => props.border};
  border-bottom: ${(props) => props.borderBottom};
  border-top: ${(props) => props.borderTop};
  border-left: ${(props) => props.borderLeft};
  border-right: ${(props) => props.borderRight};
  background-color: ${(props) => props.backgroundColor};
  border-radius: ${(props) => props.borderRadius};
  margin: ${(props) => props.margin};
  box-shadow: ${(props) => props.boxShadow};
  width: ${(props) => props.width};
  max-width: ${(props) => props.maxWidth};
  min-width: ${(props) => props.minWidth};
`;

const StyledContent = styled.div<AccordionProps>`
  display: flex;
  flex-direction: column;

  gap: ${(props) => props.gap};
  margin: ${(props) => props.margin};
  width: ${(props) => props.width};
`;

const StyledHeaderWrapper = styled.div<AccordionItem>`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;

  border: ${(props) => props.headBorder};
  border-radius: ${(props) => props.borderRadius};
  padding: ${(props) => props.padding};
`;

const StyledHeader = styled.div<AccordionProps>`
  display: flex;
  flex-wrap: wrap;
  flex: 1;
`;

const StyledIconWrapper = styled.div<AccordionItem>`
  display: flex;
  transition: ${config.transitionShort};
  align-self: center;
  transform: ${(props) =>
    props.isOpen
      ? `rotate(${props.iconRotateAngle}deg)`
      : `rotate(${props.iconStartAngle}deg)`};
  transform-origin: center;
`;

const StyledBody = styled.div<AccordionProps>`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  padding: ${(props) => `0 ${props.padding} 0 ${props.padding}`};
`;

const StyledAnimatedWrapper = styled.div<AccordionItem>`
  display: flex;
  transition: ${config.transitionShort};
  overflow: hidden;
  margin: ${(props) => (props.isOpen ? `0 0 ${props.padding} 0` : '0px')};
`;

const StyledIcon = styled.div<AccordionItem>`
  color: ${(props) => props.iconColor};
`;

const StyledNode = styled.div<AccordionItem>`
  width: ${(props) => props.width};
`;

const AccordionItem = ({
  title,
  // --------------------------------------------------------------------------------
  content,
  // --------------------------------------------------------------------------------
  isOpen,
  borderRadius = styleConfig.borderRadius,
  backgroundColor = styleConfig.backgroundColor,
  border = styleConfig.border,
  boxShadow,
  padding = styleConfig.padding,
  headBorder,
  borderBottom,
  borderTop,
  borderLeft,
  borderRight,
  margin,
  width,
  maxWidth,
  minWidth,
  icon,
  iconSize = styleConfig.iconSize,
  iconColor = styleConfig.color,
  iconStartAngle = 0,
  iconRotateAngle = styleConfig.iconRotateAngle,
  // --------------------------------------------------------------------------------
  setActive,
  idx,
  indxArray,
  setIndxArray,
  setCurrentIdx,
  collapseAll,
  // --------------------------------------------------------------------------------
  onClick,
}: AccordionItem) => {
  const [height, setHeight] = useState<number | string>(0);
  // generate 16 digit random string from date module
  const id = Math.random().toString(36).substring(2, 18) + `-${idx}`;
  console.log('⭐️ useUniqId', useUniqId());

  useEffect(() => {
    // --------------------------------------------------------------------------------
    // 📌  Apply accordion toggle & accommodate isActive state change
    // --------------------------------------------------------------------------------

    if (isOpen) {
      // get element with id animated_wrapper height
      const animatedWrapper = document.getElementById(id);
      if (animatedWrapper) {
        let scrollHeight = animatedWrapper.scrollHeight;
        setHeight(scrollHeight);
      } else {
        // default to auto height
        setHeight('auto');
      }
    } else {
      setHeight(0);
    }
  }, [isOpen, id]);

  useEffect(() => {
    // --------------------------------------------------------------------------------
    // 📌  Apply active accordion state
    // --------------------------------------------------------------------------------
    if (setActive && indxArray) {
      if (collapseAll) setIndxArray?.([...indxArray, idx as number]);
      if (!collapseAll) setCurrentIdx?.(idx as number);
    }
    // 👇️ disable the rule for a single line
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StyledAccordionWrapper
      borderRadius={borderRadius}
      backgroundColor={backgroundColor}
      border={border}
      borderBottom={borderBottom}
      borderTop={borderTop}
      borderLeft={borderLeft}
      borderRight={borderRight}
      boxShadow={boxShadow}
      padding={padding}
      margin={margin}
      width={width}
      maxWidth={maxWidth}
      minWidth={minWidth}
    >
      <StyledHeaderWrapper
        headBorder={headBorder}
        borderRadius={borderRadius}
        padding={padding}
        // --------------------------------------------------------------------------------
        onClick={onClick}
      >
        <StyledHeader>
          {!!title?.length &&
            title.map((props, key) => (
              <String
                key={key}
                string={props.string}
                // --------------------------------------------------------------------------------
                // default props
                width="100%"
                fontWeight="bold"
                color={styleConfig.color}
                cursor="pointer"
                // --------------------------------------------------------------------------------
                {...props}
              />
            ))}
        </StyledHeader>
        <StyledIconWrapper
          isOpen={isOpen}
          iconStartAngle={iconStartAngle}
          iconRotateAngle={iconRotateAngle}
        >
          {icon || (
            <StyledIcon isOpen={isOpen}>
              <Icons
                icon="plus"
                size={iconSize}
                fill={iconColor}
                onClick={() => {}}
              />
            </StyledIcon>
          )}
        </StyledIconWrapper>
      </StyledHeaderWrapper>

      <StyledAnimatedWrapper
        id={id}
        style={{ height }}
        isOpen={isOpen}
        padding={padding}
      >
        <StyledBody padding={padding}>
          {!!content?.length &&
            content.map(({ node, body, width = '100%' }, key) => {
              if (body)
                return (
                  <String
                    key={key}
                    string={body.string}
                    // --------------------------------------------------------------------------------
                    // default props
                    width="100%"
                    color={styleConfig.color}
                    // --------------------------------------------------------------------------------
                    {...body}
                  />
                );

              return (
                <StyledNode key={key} width={width}>
                  {node}
                </StyledNode>
              );
            })}
        </StyledBody>
      </StyledAnimatedWrapper>
    </StyledAccordionWrapper>
  );
};

export const Accordion: React.FC<AccordionProps> = ({
  accordion,
  margin,
  width,
  gap = styleConfig.gap,
  collapseAll = styleConfig.collapseAll,
  padding = styleConfig.padding,
  children, // allows for children to be passed in & extends the Card component
  className,
  // --------------------------------------------------------------------------------
  onClick,
}) => {
  // --------------------------------------------------------------------------------
  // 📌  Accordion component.
  // --------------------------------------------------------------------------------

  const [currentIdx, setCurrentIdx] = useState(-1);
  const [indxArray, setIndxArray] = useState<number[]>([]);

  const singleToggle = ({ idx }: ToggleProps) => {
    // if currentIdx is same as current state then update state to close current accordion item
    if (currentIdx === idx) {
      setCurrentIdx(-1);
      return;
    }
    // else update state to open current accordion item
    setCurrentIdx(idx as number);
  };

  const allToggle = ({ idx }: ToggleProps) => {
    // if idx is not in indxArray state then add to current state and update indxArray state
    if (!indxArray.includes(idx as number)) {
      setIndxArray([...indxArray, idx as number]);
      return;
    }

    // else remove idx from indxArray state and update current state
    setIndxArray(indxArray.filter((i) => i !== idx));
  };

  const onClickHandler = (idx: number) => {
    // apply all accordion toggle
    if (collapseAll) allToggle({ idx });
    // apply single accordion toggle
    if (!collapseAll) singleToggle({ idx });
  };

  const handleShow = ({ idx }: ToggleProps) => {
    let isOpen = false;

    // handle show toggle logic for single accordion items
    if (!collapseAll) isOpen = idx === currentIdx;
    // handle show toggle logic for all accordion items
    if (collapseAll) isOpen = indxArray.includes(idx);

    return isOpen;
  };

  // don't render component if no content is passed in
  if (!accordion && !children) return null;

  // map through accordion items and render them
  return (
    <StyledContent
      gap={gap}
      margin={margin}
      width={width}
      onClick={onClick}
      className={className}
    >
      {!!accordion?.length &&
        accordion.map((props, idx) => (
          <AccordionItem
            key={idx}
            // --------------------------------------------------------------------------------
            idx={idx}
            indxArray={indxArray}
            setIndxArray={setIndxArray}
            setCurrentIdx={setCurrentIdx}
            collapseAll={collapseAll}
            // --------------------------------------------------------------------------------
            isOpen={handleShow({ idx })}
            onClick={() => onClickHandler(idx)}
            // --------------------------------------------------------------------------------
            {...props}
          />
        ))}

      {!!children && <StyledBody padding={padding}>{children}</StyledBody>}
    </StyledContent>
  );
};
